import { Grid, Card, CardContent, Typography, CardActions } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import { Fragment, memo, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { PlaceForm } from 'civil-status/view-models/place';
import MunicipalityListANSC from './municipality';
import StatisticListANSC from './statistic-list';
import { DatePicker } from '@mui/lab';
import { regexOnlyNumber } from 'shell/constants/regex';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { MunicipalityAnpr } from 'shell/components/shared/municipality-list';
import { DomainType } from 'civil-status/constants/domain';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DateTime } from 'luxon';
import ListGender from './gender';
import ListAnscValoriDominio from 'civil-status/components/shared/list-ansc-valori-dominio';

interface FormPlaceComponentPops {
  title: string;
  disabled?: boolean;
  onSave?: (form?: PlaceForm) => void;
  initialValues?: PlaceForm;
  isEdit?: boolean;
  isDeath?: boolean;
  isAutority?: boolean;
  isReligiousCults?: boolean;
  isMinisterReligion?: boolean;
}

const validationSchema = yup.object({
  code: yup.string().required('Il campo è obbligatorio'),
  orderNumber: yup.string().matches(regexOnlyNumber, 'Campo numerico'),
  initialDate: yup.date().typeError('Data non valida').nullable(),
  finalDate: yup
    .date()
    .typeError('Data non valida')
    .nullable()
    .when('initialDate', (initialDate, formvalidationSchema) =>
      initialDate && initialDate?.toString() !== 'Invalid Date'
        ? formvalidationSchema.min(
            initialDate,
            'La data deve essere maggiore o uguale della Data inizio validità'
          )
        : formvalidationSchema.nullable()
    ),
  shortDescription: yup.string().required('Il campo è obbligatorio'),
  completeDescription: yup.string().required('Il campo è obbligatorio'),
  municipality: yup.object().nullable().shape({}).required('Il campo è obbligatorio'),
});

const validationSchemaAutority = yup.object({
  code: yup.string().required('Il campo è obbligatorio'),
  orderNumber: yup.string().matches(regexOnlyNumber, 'Campo numerico'),
  initialDate: yup.date().typeError('Data non valida').nullable(),
  finalDate: yup
    .date()
    .typeError('Data non valida')
    .nullable()
    .when('initialDate', (initialDate, formvalidationSchema) =>
      initialDate && initialDate?.toString() !== 'Invalid Date'
        ? formvalidationSchema.min(
            initialDate,
            'La data deve essere maggiore o uguale della Data inizio validità'
          )
        : formvalidationSchema.nullable()
    ),
  shortDescription: yup.string().required('Il campo è obbligatorio'),
  completeDescription: yup.string().required('Il campo è obbligatorio'),
  codeList: yup.string().required('Il campo è obbligatorio'),
});

const validationSchemaReligiousCults = yup.object({
  code: yup.string().required('Il campo è obbligatorio'),
  orderNumber: yup.string().matches(regexOnlyNumber, 'Campo numerico'),
  initialDate: yup.date().typeError('Data non valida').nullable(),
  finalDate: yup
    .date()
    .typeError('Data non valida')
    .nullable()
    .when('initialDate', (initialDate, formvalidationSchema) =>
      initialDate && initialDate?.toString() !== 'Invalid Date'
        ? formvalidationSchema.min(
            initialDate,
            'La data deve essere maggiore o uguale della Data inizio validità'
          )
        : formvalidationSchema.nullable()
    ),
  shortDescription: yup.string().required('Il campo è obbligatorio'),
  completeDescription: yup.string().required('Il campo è obbligatorio'),
});

const FormPlaceComponent = ({
  title,
  disabled,
  initialValues,
  onSave,
  isEdit,
  isDeath = false,
  isAutority = false,
  isReligiousCults = false,
  isMinisterReligion = false,
}: FormPlaceComponentPops): React.ReactElement => {
  const navigate = useNavigate();

  const onSubmit = useCallback(
    (formValues) => {
      if (onSave) {
        onSave(formValues);
      }
    },
    [onSave]
  );

  const { userProfile } = useUserProfile();

  const form = useFormik<PlaceForm>({
    initialValues: {
      organization: userProfile.datiLogin?.key?.codiceEnte || '',
      code: '',
      shortDescription: '',
      completeDescription: '',
      municipality: null,
      statisticCode: '',
      codeList: '',
      article: '',
      gender: '',
      orderNumber: '',
      initialDate: null,
      finalDate: null,
      ...(initialValues || {}),
    },
    validationSchema: isReligiousCults
      ? validationSchemaReligiousCults
      : isAutority || isMinisterReligion
      ? validationSchemaAutority
      : validationSchema,
    onSubmit,
  });

  const decodeMunicipalityAnpr = useHttpClient<HttpResponse<MunicipalityAnpr>>(
    `${__ACT_API_URL__}/valori-dominio-atti/comune-anpr/dominio/${DomainType.Municipality}/valore`,
    {
      options: {
        method: 'GET',
        loading: true,
        errorCatching: true,
      },
    }
  );

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={form.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Ente"
                  label="Ente *"
                  id="organization"
                  name="organization"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '2',
                  }}
                  value={form.values.organization || ''}
                  onChange={(event) => form.setFieldValue('organization', event.target.value)}
                  error={form.touched.organization && Boolean(form.errors.organization)}
                  helperText={form.errors.organization}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Codice"
                  label="Codice *"
                  id="code"
                  name="code"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '10',
                  }}
                  variant="outlined"
                  value={form.values.code || ''}
                  onChange={form.handleChange}
                  error={form.touched.code && Boolean(form.errors.code)}
                  helperText={form.errors.code}
                  disabled={isEdit || disabled}
                />
              </Grid>
              <Grid item xs={12} md={isMinisterReligion ? 4 : 8}>
                <PmiTextField
                  label={
                    isMinisterReligion
                      ? 'Nome *'
                      : isDeath
                      ? 'Descrizione *'
                      : 'Descrizione breve *'
                  }
                  aria-label={
                    isMinisterReligion ? 'Nome' : isDeath ? 'Descrizione' : 'Descrizione breve'
                  }
                  id="shortDescription"
                  name="shortDescription"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '100',
                  }}
                  variant="outlined"
                  value={form.values.shortDescription || ''}
                  onChange={form.handleChange}
                  error={form.touched.shortDescription && Boolean(form.errors.shortDescription)}
                  helperText={form.errors.shortDescription}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={isMinisterReligion ? 4 : 12}>
                <PmiTextField
                  aria-label={
                    isMinisterReligion ? 'Cognome' : isDeath ? 'Indirizzo' : 'Descrizione'
                  }
                  label={
                    isMinisterReligion ? 'Cognome *' : isDeath ? 'Indirizzo *' : 'Descrizione *'
                  }
                  id="completeDescription"
                  name="completeDescription"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={form.values.completeDescription || ''}
                  onChange={(event) =>
                    form.setFieldValue('completeDescription', event.target.value)
                  }
                  error={
                    form.touched.completeDescription && Boolean(form.errors.completeDescription)
                  }
                  helperText={form.errors.completeDescription}
                  disabled={disabled}
                />
              </Grid>
              {!isAutority && !isReligiousCults && !isMinisterReligion ? (
                <Fragment>
                  <Grid item xs={12} md={5}>
                    <MunicipalityListANSC
                      required
                      value={form.values.municipality || null}
                      onChange={(el) => {
                        (
                          decodeMunicipalityAnpr({
                            url: `/${el?.id}`,
                          }) as ResponsePromise<HttpResponse<MunicipalityAnpr>>
                        ).then((response) => {
                          const municipality = response?.data.payload?.denominazione;

                          const formattedStartDate = response?.data.payload?.dataIstituzione
                            ? DateTime.fromISO(response?.data.payload?.dataIstituzione).toFormat(
                                'dd/MM/yyyy'
                              )
                            : '';

                          const formattedEndDate = response?.data.payload?.dataCessazione
                            ? DateTime.fromISO(response?.data.payload?.dataCessazione).toFormat(
                                'dd/MM/yyyy'
                              )
                            : '';
                          const descriptionAnpr = `${municipality} ${
                            formattedStartDate && formattedEndDate
                              ? `${formattedStartDate} - ${formattedEndDate}`
                              : ''
                          }`;

                          form.setFieldValue('municipality', {
                            ...el,
                            descriptionAnpr,
                            province: response?.data?.payload?.codiceSiglaProvincia,
                          });
                        });
                      }}
                      error={form.touched.municipality && Boolean(form.errors.municipality)}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <PmiTextField
                      aria-label="Nome comune ANPR"
                      label="Nome comune ANPR"
                      id="nameMunicipalityANPR"
                      name="nameMunicipalityANPR"
                      fullWidth
                      type="text"
                      variant="outlined"
                      value={form.values.municipality?.descriptionAnpr || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <PmiTextField
                      aria-label="Provincia"
                      label="Provincia"
                      id="province"
                      name="province"
                      fullWidth
                      type="text"
                      variant="outlined"
                      value={form.values.municipality?.province || ''}
                      disabled
                    />
                  </Grid>
                </Fragment>
              ) : undefined}
              {!isAutority && !isMinisterReligion && !isMinisterReligion && !isReligiousCults ? (
                <Grid item container spacing={1} xs={12}>
                  <Grid item xs={12} md={6}>
                    <StatisticListANSC
                      value={form.values.statisticCode || ''}
                      onChange={(el) => form.setFieldValue('statisticCode', el)}
                      error={form.touched.statisticCode && Boolean(form.errors.statisticCode)}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              ) : undefined}
              {isReligiousCults ? (
                <Grid item container spacing={1} xs={12}>
                  <Grid item xs={12} md={6}>
                    <PmiTextField
                      aria-label="Codice Statistico"
                      label="Codice Statistico"
                      id="statisticCode"
                      name="statisticCode"
                      fullWidth
                      type="text"
                      variant="outlined"
                      value={form.values.statisticCode || ''}
                      onChange={(event) => form.setFieldValue('statisticCode', event.target.value)}
                      error={form.touched.statisticCode && Boolean(form.errors.statisticCode)}
                      helperText={form.errors.statisticCode}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              ) : undefined}
              {!isAutority && !isReligiousCults && !isMinisterReligion ? (
                <Fragment>
                  <Grid item xs={12} md={2}>
                    <PmiTextField
                      aria-label="Articolo anteposto"
                      label="Articolo anteposto"
                      id="article"
                      name="article"
                      fullWidth
                      type="text"
                      variant="outlined"
                      inputProps={{
                        maxLength: '5',
                      }}
                      value={form.values.article || ''}
                      onChange={(event) => form.setFieldValue('article', event.target.value)}
                      error={form.touched.article && Boolean(form.errors.article)}
                      helperText={form.errors.article}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <ListGender
                      label="Genere"
                      value={form.values.gender || ''}
                      onChange={(event) => form.setFieldValue('gender', event.target.value)}
                      error={form.touched.gender && Boolean(form.errors.gender)}
                      disabled={disabled}
                    />
                  </Grid>
                </Fragment>
              ) : undefined}
              {isAutority ? (
                <Grid item xs={12} md={4}>
                  <ListAnscValoriDominio
                    value={form.values.codeList || ''}
                    onChange={(el) => form.setFieldValue('codeList', el)}
                    error={form.touched.codeList && Boolean(form.errors.codeList)}
                    disabled={disabled}
                    idDominio={DomainType.Deathplace}
                    required
                  />
                </Grid>
              ) : undefined}
              {isMinisterReligion ? (
                <Grid item xs={12} md={6}>
                  <ListAnscValoriDominio
                    label="Culto religioso"
                    value={form.values.codeList || ''}
                    onChange={(el) => form.setFieldValue('codeList', el)}
                    error={form.touched.codeList && Boolean(form.errors.codeList)}
                    disabled={disabled}
                    idDominio={DomainType.ReligiousCults}
                    required
                  />
                </Grid>
              ) : undefined}
              {!isMinisterReligion ? (
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="N. ordinamento"
                    label="N. ordinamento"
                    id="orderNumber"
                    name="orderNumber"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={form.values.orderNumber || ''}
                    onChange={(event) => form.setFieldValue('orderNumber', event.target.value)}
                    error={form.touched.orderNumber && Boolean(form.errors.orderNumber)}
                    helperText={form.errors.orderNumber}
                    disabled={disabled}
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Data inizio validità"
                  aria-label="Data inizio validità"
                  value={form.values.initialDate || null}
                  disabled={disabled}
                  onChange={(date) => form.setFieldValue('initialDate', date)}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="initialDate"
                      fullWidth
                      error={form.touched.initialDate && Boolean(form.errors.initialDate)}
                      helperText={form.errors.initialDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Data fine validità"
                  aria-label="Data fine validità"
                  value={form.values.finalDate || null}
                  onChange={(date) => form.setFieldValue('finalDate', date)}
                  disabled={disabled}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="finalDate"
                      fullWidth
                      error={form.touched.finalDate && Boolean(form.errors.finalDate)}
                      helperText={form.errors.finalDate}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <PmiButton
                variant="outlined"
                fullWidth
                color="cdmBrandThird"
                type="submit"
                disabled={disabled}
              >
                Conferma
              </PmiButton>
            </Grid>
            <Grid item xs={12} md={2}>
              <PmiButton
                variant="outlined"
                color="cdmBrand"
                fullWidth
                onClick={() => {
                  if (isDeath) {
                    navigate('/stato-civile/luogo-decesso');
                    return;
                  }

                  if (isAutority) {
                    navigate('/stato-civile/autorita-decesso');
                    return;
                  }

                  if (isReligiousCults) {
                    navigate('/stato-civile/culti-religiosi');
                    return;
                  }

                  if (isMinisterReligion) {
                    navigate('/stato-civile/ministro-culto');
                    return;
                  }

                  navigate('/stato-civile/luogo-nascita');
                }}
              >
                Annulla
              </PmiButton>
            </Grid>
          </Grid>
        </CardActions>
      </form>
    </Card>
  );
};

FormPlaceComponent.displayName = 'Form place';

const FormPlace = memo(FormPlaceComponent);
export default FormPlace;
